import { useCallback, useEffect, useMemo } from "react";
import {
  Alert,
  Button,
  Group,
  Modal,
  NumberInput,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  accessioningService,
  type CaseIdentityResult,
} from "../../services/workflows.service";
import {
  formatCaseNumber,
  MAX_CASE_NUMBER_SEQUENCE,
  MIN_CASE_NUMBER_SEQUENCE,
  parseCaseNumber,
  validateCaseNumberSequence,
  validateSequenceChangeReason,
} from "../../utils/caseNumberSequence";

interface CaseNumberCorrectionModalProps {
  opened: boolean;
  onClose: () => void;
  caseId: string;
  currentCaseNumber: string;
  onSuccess: (result: CaseIdentityResult) => void;
}

interface CorrectionFormValues {
  sequence: number | string;
  reason: string;
}

export default function CaseNumberCorrectionModal({
  opened,
  onClose,
  caseId,
  currentCaseNumber,
  onSuccess,
}: CaseNumberCorrectionModalProps) {
  const queryClient = useQueryClient();
  const identity = useMemo(
    () => parseCaseNumber(currentCaseNumber),
    [currentCaseNumber]
  );
  const form = useForm<CorrectionFormValues>({
    initialValues: { sequence: identity?.sequence ?? "", reason: "" },
    validateInputOnBlur: true,
    validate: {
      sequence: (value) => {
        const validationError = validateCaseNumberSequence(value);
        if (validationError) return validationError;
        const number = Number(value);
        if (number === identity?.sequence)
          return "Enter a different sequence number";
        return null;
      },
      reason: (value) => validateSequenceChangeReason(value),
    },
  });
  const { clearErrors, setValues } = form;

  useEffect(() => {
    if (!opened) return;
    setValues({ sequence: identity?.sequence ?? "", reason: "" });
    clearErrors();
  }, [opened, identity?.sequence, setValues, clearErrors]);

  const mutation = useMutation({
    mutationFn: (values: CorrectionFormValues) =>
      accessioningService.correctCaseNumber(caseId, {
        sequence: Number(values.sequence),
        reason: values.reason.trim(),
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["case", caseId] });
      onSuccess(result);
      onClose();
    },
  });

  const handleSubmit = useCallback(
    (values: CorrectionFormValues) => mutation.mutate(values),
    [mutation]
  );

  const preview =
    identity && Number.isInteger(Number(form.values.sequence))
      ? formatCaseNumber(identity, Number(form.values.sequence))
      : "—";

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Correct case number"
      closeOnClickOutside={false}
      centered
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <Alert color="yellow" icon={<IconAlertTriangle size={18} />}>
            This changes the case identifier. Reprint any labels or documents
            that show the old number.
          </Alert>
          <Text size="sm">
            Current case number: <strong>{currentCaseNumber}</strong>
          </Text>
          <NumberInput
            label="Sequence number"
            description="Choose an unused number in this prefix and year."
            min={MIN_CASE_NUMBER_SEQUENCE}
            max={MAX_CASE_NUMBER_SEQUENCE}
            allowDecimal={false}
            required
            {...form.getInputProps("sequence")}
          />
          <Text size="sm" c="dimmed">
            New case number: <strong>{preview}</strong>
          </Text>
          <Textarea
            label="Reason for correction"
            placeholder="Explain why this number is being changed"
            required
            autosize
            minRows={2}
            {...form.getInputProps("reason")}
          />
          {mutation.error && (
            <Alert color="red">
              {mutation.error instanceof Error
                ? mutation.error.message
                : "The case number could not be changed. Please try again."}
            </Alert>
          )}
          <Group justify="space-between" mt="xs">
            <Button
              variant="subtle"
              onClick={onClose}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              Save case number
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
